import { useI18n } from "../lib/i18n";
import { Sparkles, Users, Globe2, Music, Paintbrush, Disc3, Mic2, Handshake } from "lucide-react";
import { BreadcrumbJsonLd } from "../components/JsonLd";
import { FaqSection } from "../components/FaqSection";

const FAQ_ITEMS = [
  {
    q: "Qui peut rejoindre l'association Fedevo 2.0 ?",
    a: "Toute personne intéressée par les cultures urbaines : artistes (rappeurs, danseurs, DJ, graffeurs, beatmakers, beatboxers), experts, organismes ou simples passionnés. Aucune condition de diplôme ou de niveau — seule compte la motivation à <strong>faire rayonner la culture</strong>.",
  },
  {
    q: "Quels sont les âges accueillis ?",
    a: "L'association s'adresse principalement aux <strong>jeunes créateurs</strong>, mais ses événements, diffusions et partenariats sont ouverts à tous les âges. Nous accompagnons les talents émergents dès l'adolescence jusqu'aux artistes confirmés.",
  },
  {
    q: "Y a-t-il des ateliers ou des formations proposés ?",
    a: "Oui, à travers notre réseau de partenaires (Graff-ik-Art, Alliance Musik, Collectif Unité Hip-Hop Lyonnais) nous organisons régulièrement des <strong>ateliers graffiti, workshops rap, masterclass DJ et battles breakdance</strong>. Inscrivez-vous à la newsletter pour être tenu informé des prochaines dates.",
  },
  {
    q: "Quelle est la différence entre FVHHCU et Fedevo 2.0 ?",
    a: "La <strong>FVHHCU</strong> (Fédération Vaudaise du Hip-Hop et des Cultures Urbaines) est la structure fondée en 2010. <strong>Fedevo 2.0</strong> est son évolution digitale lancée en 2020 : une webTV + un média urbain streaming indépendant financièrement, porté par RBY Concept et révisé pour l'ère numérique.",
  },
  {
    q: "L'association est-elle indépendante financièrement ?",
    a: "Oui. Fedevo 2.0 fonctionne grâce à des <strong>fonds privés et des collaborations</strong> avec nos partenaires, sans dépendance à un financement public unique. Les adhésions, le Pack Créateur et le Pack Premium participent directement à cette indépendance.",
  },
  {
    q: "Comment soutenir Fedevo 2.0 ?",
    a: "Plusieurs façons : devenir <strong>membre (5 €)</strong>, passer au <strong>Pack Créateur</strong> pour diffuser vos vidéos, devenir <strong>partenaire / sponsor</strong>, partager nos chaînes sur vos réseaux, ou simplement regarder et commenter les diffusions — chaque vue compte.",
  },
  {
    q: "Fedevo 2.0 diffuse-t-il uniquement du contenu français ?",
    a: "Le site est <strong>francophone par défaut</strong> (toggle FR/EN disponible en haut de page), mais notre catalogue inclut du contenu international : hip-hop US, battles internationales, interviews artistes francophones et anglophones. La culture urbaine est par nature globale.",
  },
  {
    q: "Où consulter les événements à venir ?",
    a: "Inscrivez-vous à la newsletter en bas de page — chaque envoi contient le programme des diffusions à venir, les battles et les ateliers partenaires. Les chaînes en direct (page Accueil) annoncent aussi les prochains contenus majeurs.",
  },
];

export default function About() {
  const { t } = useI18n();
  const disciplines = [
    { icon: Mic2, label: t("about.d.rap") },
    { icon: Users, label: t("about.d.dance") },
    { icon: Disc3, label: t("about.d.dj") },
    { icon: Paintbrush, label: t("about.d.graff") },
    { icon: Music, label: t("about.d.beat") },
    { icon: Sparkles, label: t("about.d.beatbox") },
  ];
  const stats = [
    { n: "2010", label: t("about.stat.founded") },
    { n: "15+", label: t("about.stat.years") },
    { n: "2020", label: t("about.stat.digital") },
    { n: "∞", label: t("about.stat.talents") },
  ];
  return (
    <div>
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: t("about.eyebrow"), path: "/about" }]} />
      <section className="relative overflow-hidden border-b border-[color:var(--border)]">
        <div className="max-w-[1100px] mx-auto px-6 py-16 lg:py-24 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-4">{t("about.eyebrow")}</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            {t("about.h1.part1")}<br />
            <span className="text-[color:var(--accent)]">{t("about.h1.hiphop")}</span> {t("about.h1.part2")}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[color:var(--text-muted)] max-w-2xl mx-auto">
            {t("about.lead1")} <span className="text-white font-medium">FEDEVO</span> {t("about.lead2")}
          </p>
        </div>
      </section>

      <section className="border-b border-[color:var(--border)] bg-[color:var(--bg-elev)]">
        <div className="max-w-[1100px] mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center stagger">
          {stats.map((s) => (
            <div key={s.label} data-testid={`about-stat-${s.label}`}>
              <div className="font-display text-5xl lg:text-6xl text-[color:var(--accent)]">{s.n}</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--text-muted)] mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 py-16 grid lg:grid-cols-[1fr_1.7fr] gap-10" data-testid="about-body">
        <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Handshake className="w-4 h-4 text-[color:var(--accent)]" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--text-muted)]">{t("about.dna.eyebrow")}</span>
            </div>
            <p className="font-display text-2xl leading-tight">{t("about.dna.text")}</p>
          </div>
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[color:var(--accent)]" />
              <span className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--text-muted)]">{t("about.disciplines")}</span>
            </div>
            <ul className="grid grid-cols-2 gap-3">
              {disciplines.map((d) => {
                const Icon = d.icon;
                return (
                  <li key={d.label} className="flex items-center gap-2 text-sm">
                    <Icon className="w-4 h-4 text-[color:var(--accent)] shrink-0" /> {d.label}
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <article className="space-y-6 text-[15px] leading-relaxed text-[color:var(--text)]">
          <p>{t("about.p1")}</p>
          <p>
            {t("about.p2a")} <span className="text-[color:var(--accent)]">{t("about.p2disciplines")}</span> {t("about.p2b")}
          </p>
          <p className="text-[color:var(--text-muted)]">{t("about.p3")}</p>

          <blockquote className="border-l-4 border-[color:var(--accent)] pl-6 py-2 my-8">
            <p className="font-display text-2xl lg:text-3xl leading-tight">
              {t("about.quote1a")} <span className="text-[color:var(--accent)]">{t("about.quote1b")}</span> {t("about.quote1c")}
            </p>
          </blockquote>

          <p>
            <span className="font-display text-2xl text-[color:var(--accent)]">FEDEVO 2.0</span>, {t("about.p4")}
          </p>

          <div className="mt-10 rounded-2xl p-8 text-center" style={{ background: "linear-gradient(135deg, rgba(240,180,41,0.12), rgba(20,163,163,0.08))", border: "1px solid rgba(240,180,41,0.3)" }}>
            <Globe2 className="w-7 h-7 mx-auto text-[color:var(--accent)] mb-3" />
            <p className="font-display text-3xl lg:text-4xl">« {t("about.goal.quote")} »</p>
            <div className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[color:var(--text-muted)]">{t("about.goal.label")}</div>
          </div>
        </article>
      </section>

      <section className="border-t border-[color:var(--border)] py-14 text-center bg-[color:var(--bg-elev)]">
        <div className="max-w-[900px] mx-auto px-6">
          <h2 className="font-display text-4xl mb-3">{t("about.cta.title")}</h2>
          <p className="text-[color:var(--text-muted)] mb-6 max-w-xl mx-auto">{t("about.cta.body")}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="/membership" className="bg-[color:var(--accent)] text-black font-semibold px-6 py-3 rounded-full text-xs uppercase tracking-widest hover:brightness-110 transition">
              {t("about.cta.membership")}
            </a>
            <a href="/partners" className="border border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] px-6 py-3 rounded-full text-xs uppercase tracking-widest transition">
              {t("about.cta.partners")}
            </a>
          </div>
        </div>
      </section>

      <FaqSection title="À propos de l'association" items={FAQ_ITEMS} />
    </div>
  );
}
